"use client"

import * as React from "react"
import { CheckIcon, LayoutGridIcon, PanelLeftIcon, PhoneIcon, SearchIcon } from "lucide-react"

import { initialsOf } from "@/features/dashboard/components/mobile-app-header"
import { getStatusBadgeClassName } from "@/features/members/components/member-status-styles"
import { MemberStatus, type Member } from "@/features/members/model/members-types"
import { useTranslations } from "@/i18n"
import { LoadingRegion, SkeletonLine } from "@/shared/components/skeletons"
import { type SelectableListItemProps, useSelectableListKeyboard } from "@/shared/hooks/use-selectable-list-keyboard"
import { avatarTintFor } from "@/shared/lib/avatar-tint"
import { cn } from "@/shared/lib/utils"
import { Button, Input, SegmentedControl, Skeleton } from "@ibada/ui"

/**
 * How the members list is drawn. Desktop offers cards and list (kept in the URL, `?view=`, so a shared link opens
 * the same one); the table remains only where there is no switch: read-only inspection (Branch Details).
 */
export enum MembersView {
  Cards = "cards",
  List = "list",
  Table = "table",
}

export const DEFAULT_MEMBERS_VIEW = MembersView.Cards

/** The views the desktop switch offers. */
const SWITCHABLE_VIEWS = [MembersView.Cards, MembersView.List] as const

export function parseMembersView(value: string | null): MembersView {
  return SWITCHABLE_VIEWS.find((view) => view === value) ?? DEFAULT_MEMBERS_VIEW
}

const VIEW_ICONS: Record<(typeof SWITCHABLE_VIEWS)[number], React.ReactNode> = {
  [MembersView.Cards]: <LayoutGridIcon className='size-4' aria-hidden />,
  [MembersView.List]: <PanelLeftIcon className='size-4' aria-hidden />,
}

/** Cards / List as one segmented pill track, drawn like the list's status tabs beside it. */
export function MembersViewSwitch({ value, onChange }: { value: MembersView; onChange: (view: MembersView) => void }) {
  const t = useTranslations("people.members.views")
  return (
    <div className='shrink-0' aria-label={t("label")}>
      <SegmentedControl
        value={value}
        onValueChange={(next) => onChange(parseMembersView(next))}
        items={SWITCHABLE_VIEWS.map((view) => ({
          value: view,
          label: (
            <span className='inline-flex items-center gap-1.5'>
              {VIEW_ICONS[view]}
              {t(view)}
            </span>
          ),
        }))}
        variant='table-view'
        size='sm'
        itemClassName='whitespace-nowrap px-3'
      />
    </div>
  )
}

function ageOf(dateOfBirth: string | undefined): number | null {
  if (!dateOfBirth) return null
  const birth = new Date(dateOfBirth)
  if (Number.isNaN(birth.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age -= 1
  return age >= 0 && age < 130 ? age : null
}

function yearOf(value: string | null | undefined): number | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.getFullYear()
}

function affiliationsOf(member: Member): string[] {
  return [...(member.groupNames ?? []), ...(member.ministryNames ?? [])].filter(Boolean)
}

/** Anything with a name and an optional photo: a full member, or a member search result. */
export type MemberAvatarSubject = { name: string; avatarUrl?: string | null }

export function MemberAvatar({ member, className }: { member: MemberAvatarSubject; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-extrabold",
        avatarTintFor(member.name),
        className,
      )}
    >
      {member.avatarUrl ? <img src={member.avatarUrl} alt='' className='size-full object-cover' /> : initialsOf(member.name)}
    </span>
  )
}

/** Up to `max` group / ministry chips, then "+N". */
function AffiliationChips({ names, max = 2 }: { names: string[]; max?: number }) {
  const shown = names.slice(0, max)
  const extra = names.length - shown.length
  return (
    <span className='flex min-w-0 flex-wrap gap-1'>
      {shown.map((name) => (
        <span key={name} className='bg-muted text-foreground/80 max-w-[10rem] truncate rounded-full px-2 py-0.5 text-xs font-medium'>
          {name}
        </span>
      ))}
      {extra > 0 ? <span className='text-muted-foreground px-1 py-0.5 text-xs font-medium'>+{extra}</span> : null}
    </span>
  )
}

/** The status pill, only when it says something: in the members tab nearly everyone is active. */
export function StatusPill({ status }: { status: MemberStatus }) {
  const t = useTranslations("people.members")
  if (status === MemberStatus.Active) return null
  return (
    <span className={cn("inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[11px] font-bold", getStatusBadgeClassName(status))}>
      {t(`statuses.${status}` as never)}
    </span>
  )
}

function useMemberSummary(member: Member) {
  const t = useTranslations("people.members.preview")
  const age = ageOf(member.dateOfBirth)
  const occupation = member.occupation?.trim() || null
  const line = [occupation ?? t("noOccupation"), age != null ? t("age", { age }) : null].filter(Boolean).join(" · ")
  return { age, occupation, line }
}

// ---------------------------------------------------------------------------------------------------------- cards

/** Cards view (approved design C): three member cards per row; a click opens the member. */
/** Quick confirm for a member awaiting confirmation; absent for someone who cannot edit members. */
export type MemberConfirmAction = {
  onConfirm: (member: Member) => void
  /** The member being confirmed right now, whose button is busy. */
  confirmingId: string | null
}

function ConfirmMemberButton({ member, action }: { member: Member; action: MemberConfirmAction }) {
  const t = useTranslations("people.members.preview")
  return (
    <Button
      size='sm'
      className='h-7 px-2.5 text-xs'
      isLoading={action.confirmingId === member.id}
      onClick={(event) => {
        event.stopPropagation()
        action.onConfirm(member)
      }}
    >
      {action.confirmingId === member.id ? null : <CheckIcon className='size-3.5' aria-hidden />}
      {t("confirm")}
    </Button>
  )
}

export function MemberCardGrid({
  members,
  isInitialLoading,
  emptyState,
  onOpen,
  confirm,
}: {
  members: Member[]
  isInitialLoading: boolean
  emptyState: React.ReactNode
  onOpen: (member: Member) => void
  confirm?: MemberConfirmAction
}) {
  if (isInitialLoading) {
    return (
      // The member card's own layout: avatar, name, a line, the date column on the right.
      <LoadingRegion className='grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3'>
        {Array.from({ length: 9 }).map((_, index) => (
          <div key={index} className='border-border/60 bg-card flex h-[5.5rem] items-start gap-3 rounded-xl border p-3.5'>
            <Skeleton className='size-11 shrink-0 rounded-full' />
            <span className='flex min-w-0 flex-1 flex-col gap-2 pt-1'>
              <SkeletonLine className={cn("h-3.5", index % 3 === 0 ? "w-3/5" : index % 3 === 1 ? "w-1/2" : "w-2/3")} />
              <SkeletonLine className='w-2/5' />
            </span>
            <SkeletonLine className='w-12 shrink-0' />
          </div>
        ))}
      </LoadingRegion>
    )
  }
  if (members.length === 0) return <>{emptyState}</>
  return (
    <ul className='grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3'>
      {members.map((member) => (
        <li key={member.id}>
          <MemberCard member={member} onOpen={onOpen} confirm={confirm} />
        </li>
      ))}
    </ul>
  )
}

function MemberCard({
  member,
  onOpen,
  confirm,
}: {
  member: Member
  onOpen: (member: Member) => void
  confirm?: MemberConfirmAction
}) {
  const t = useTranslations("people.members.preview")
  const { line } = useMemberSummary(member)
  const affiliations = affiliationsOf(member)
  const canConfirm = Boolean(confirm) && member.status === MemberStatus.PendingConfirmation
  return (
    // The whole card opens the member (a full-size button underneath); a pending member's Confirmer sits on top
    // of it as its own control, so neither is nested in the other.
    <div className='border-border/60 bg-card hover:border-border relative flex h-full w-full items-start gap-3 rounded-xl border p-3.5 text-left transition-colors hover:shadow-sm'>
      <button type='button' aria-label={member.name} onClick={() => onOpen(member)} className='absolute inset-0 rounded-xl' />
      <MemberAvatar member={member} className='pointer-events-none size-11 text-sm' />
      <span className='pointer-events-none flex min-w-0 flex-1 flex-col gap-1'>
        <span className='flex min-w-0 items-center gap-2'>
          <span className='truncate font-bold'>{member.name}</span>
          <StatusPill status={member.status} />
        </span>
        <span className='text-muted-foreground truncate text-xs'>{line}</span>
        {affiliations.length > 0 ? (
          <span className='mt-1'>
            <AffiliationChips names={affiliations} />
          </span>
        ) : null}
      </span>
      <span className='text-muted-foreground pointer-events-none flex shrink-0 flex-col items-end gap-1.5 self-stretch text-xs'>
        {member.baptized === true ? (
          <span className='text-primary font-semibold'>{t("baptized")}</span>
        ) : member.baptized === false ? (
          <span>{t("notBaptized")}</span>
        ) : null}
        {member.phone ? <span className='tabular-nums'>{member.phone}</span> : null}
        {canConfirm && confirm ? (
          <span className='pointer-events-auto relative z-10 mt-auto pt-1'>
            <ConfirmMemberButton member={member} action={confirm} />
          </span>
        ) : null}
      </span>
    </div>
  )
}

// ----------------------------------------------------------------------------------------------- list + preview

/**
 * List view (approved design B): the page's members on the left, the selected one's summary on the right with
 * call / WhatsApp / edit and a way into the full profile. Selection is local; it starts on the first member.
 */
export function MemberListPreview({
  members,
  isInitialLoading,
  emptyState,
  search,
  onSearchChange,
  searchPlaceholder,
  onOpen,
  onEdit,
  confirm,
}: {
  members: Member[]
  isInitialLoading: boolean
  emptyState: React.ReactNode
  /** The list's search, drawn at the top of the list column (the toolbar hides its own in this view). */
  search: string
  onSearchChange: (value: string) => void
  searchPlaceholder: string
  onOpen: (member: Member) => void
  /** Present only for someone allowed to edit members. */
  onEdit?: (member: Member) => void
  confirm?: MemberConfirmAction
}) {
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const selected = members.find((member) => member.id === selectedId) ?? members[0]
  const t = useTranslations("people.members.preview")

  // Keyboard: arrows move the selection (and the preview follows), Home / End and Page Up / Down jump, typing a
  // name jumps to it, Enter opens the profile. ArrowDown from the search field lands in the list.
  const list = useSelectableListKeyboard({
    items: members,
    getId: (member) => member.id,
    getLabel: (member) => member.name,
    selectedId: selected?.id,
    onSelect: (member) => setSelectedId(member.id),
    onActivate: onOpen,
  })
  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "ArrowDown" || members.length === 0) return
    event.preventDefault()
    list.focusSelected()
  }

  return (
    // The list scrolls inside its own column, so the preview beside it stays in view without `sticky` (the table's
    // clipping wrappers would break it) and both columns always start on the same line.
    <div className='grid items-start gap-3 lg:grid-cols-[22rem_minmax(0,1fr)]'>
      <div className='border-border/60 bg-card flex flex-col overflow-hidden rounded-xl border lg:max-h-[calc(100dvh-14rem)]'>
        <div className='border-border/60 border-b p-2.5'>
          <DebouncedSearchInput value={search} onChange={onSearchChange} placeholder={searchPlaceholder} onKeyDown={onSearchKeyDown} />
        </div>
        {isInitialLoading ? (
          <LoadingRegion className='space-y-1 p-2.5'>
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className='flex h-12 items-center gap-2.5 px-2'>
                <Skeleton className='size-8 shrink-0 rounded-full' />
                <span className='flex min-w-0 flex-1 flex-col gap-1.5'>
                  <SkeletonLine className={index % 2 === 0 ? "w-1/2" : "w-2/5"} />
                  <SkeletonLine className='h-2.5 w-1/3' />
                </span>
              </div>
            ))}
          </LoadingRegion>
        ) : members.length === 0 ? (
          <div className='p-2'>{emptyState}</div>
        ) : (
          <ul className='divide-border/60 min-h-0 divide-y overflow-y-auto' aria-label={t("listLabel")} {...list.getListProps()}>
            {members.map((member, index) => {
              const active = member.id === selected?.id
              return (
                <li key={member.id} role='presentation'>
                  <MemberListRow member={member} active={active} itemProps={list.getItemProps(member, index)} />
                </li>
              )
            })}
          </ul>
        )}
      </div>
      {selected && !isInitialLoading ? <MemberPreviewPanel member={selected} onOpen={onOpen} onEdit={onEdit} confirm={confirm} /> : null}
    </div>
  )
}

const SEARCH_DEBOUNCE_MS = 300

/** A search field that types freely and hands the value on once typing pauses, like the toolbar's search. */
export function DebouncedSearchInput({
  value,
  onChange,
  placeholder,
  onKeyDown,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
}) {
  const [draft, setDraft] = React.useState(value)
  const onChangeRef = React.useRef(onChange)
  onChangeRef.current = onChange

  // A change from outside (filters reset, back button) replaces the draft.
  React.useEffect(() => setDraft(value), [value])

  React.useEffect(() => {
    if (draft === value) return
    const timer = window.setTimeout(() => onChangeRef.current(draft), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draft, value])

  return (
    <div className='relative'>
      <SearchIcon className='text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2' aria-hidden />
      <Input
        type='search'
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        onKeyDown={onKeyDown}
        className='pl-9'
      />
    </div>
  )
}

function MemberListRow({
  member,
  active,
  itemProps,
}: {
  member: Member
  active: boolean
  /** Role, selection, roving tab stop, ref and click from `useSelectableListKeyboard`. */
  itemProps: SelectableListItemProps
}) {
  const t = useTranslations("people.members.preview")
  const affiliations = affiliationsOf(member)
  const detail = [member.occupation?.trim() || t("noOccupation"), affiliations.join(", ") || null].filter(Boolean).join(" · ")
  return (
    <button
      type='button'
      {...itemProps}
      className={cn(
        "flex w-full items-center gap-3 px-3.5 py-2.5 text-left transition-colors",
        active ? "bg-primary/10 shadow-[inset_3px_0_0_var(--primary)]" : "hover:bg-muted/50",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
      )}
    >
      <MemberAvatar member={member} className='size-9 text-xs' />
      <span className='flex min-w-0 flex-1 flex-col'>
        <span className='flex min-w-0 items-center gap-2'>
          <span className='truncate text-sm font-bold'>{member.name}</span>
          <StatusPill status={member.status} />
        </span>
        <span className='text-muted-foreground truncate text-xs'>{detail}</span>
      </span>
    </button>
  )
}

/** WhatsApp needs the country code: a local number ("0997…") gets no link rather than a wrong one. */
export function whatsappHref(phone: string): string | null {
  if (!phone.startsWith("+")) return null
  const digits = phone.replace(/[^\d]/g, "")
  return digits.length >= 8 ? `https://wa.me/${digits}` : null
}

function MemberPreviewPanel({
  member,
  onOpen,
  onEdit,
  confirm,
}: {
  member: Member
  onOpen: (member: Member) => void
  onEdit?: (member: Member) => void
  confirm?: MemberConfirmAction
}) {
  const t = useTranslations("people.members.preview")
  const tMembers = useTranslations("people.members")
  const { line } = useMemberSummary(member)
  const baptizedYear = yearOf(member.baptizedAt)
  const affiliations = affiliationsOf(member)
  const phone = member.phone?.trim() || null

  const baptism =
    member.baptized === true
      ? baptizedYear
        ? t("baptizedIn", { year: baptizedYear })
        : t("baptized")
      : member.baptized === false
        ? t("notBaptized")
        : t("unknown")
  const marital = member.maritalStatus ? tMembers(`maritalStatuses.${member.maritalStatus}` as never) : t("unknown")

  return (
    <section className='border-border/60 bg-card flex flex-col gap-5 rounded-xl border p-6'>
      <div className='flex items-center gap-4'>
        <MemberAvatar member={member} className='size-16 text-xl' />
        <div className='min-w-0 flex-1'>
          <div className='flex min-w-0 items-center gap-2'>
            <h2 className='truncate text-xl font-bold'>{member.name}</h2>
            <StatusPill status={member.status} />
          </div>
          <p className='text-muted-foreground truncate text-sm'>
            {line}
          </p>
        </div>
        <span className='flex shrink-0 items-center gap-2'>
          {confirm && member.status === MemberStatus.PendingConfirmation ? (
            <ConfirmMemberButton member={member} action={confirm} />
          ) : null}
          <Button onClick={() => onOpen(member)}>{t("openProfile")}</Button>
        </span>
      </div>

      <div className='flex flex-wrap gap-2'>
        <MemberContactButtons phone={phone} />
        {onEdit ? (
          <Button variant='outline' size='sm' onClick={() => onEdit(member)}>
            {t("edit")}
          </Button>
        ) : null}
      </div>

      <dl className='grid gap-2.5 sm:grid-cols-3'>
        <PreviewFact label={tMembers("columns.phone")} value={phone ?? t("noPhone")} muted={!phone} />
        <PreviewFact label={t("baptism")} value={baptism} muted={member.baptized == null} />
        <PreviewFact label={tMembers("columns.maritalStatus")} value={marital} muted={!member.maritalStatus} />
      </dl>

      <div className='space-y-2'>
        <h3 className='text-sm font-bold'>{t("groups")}</h3>
        {affiliations.length > 0 ? (
          <AffiliationChips names={affiliations} max={8} />
        ) : (
          <p className='text-muted-foreground text-sm'>{t("noGroups")}</p>
        )}
      </div>
    </section>
  )
}

/**
 * Call and WhatsApp for a member's phone: Call whenever there is a number, WhatsApp only for an international
 * (+) number. Rendered as bare buttons so the caller decides the row they sit in.
 */
export function MemberContactButtons({ phone, buttonClassName }: { phone: string | null; buttonClassName?: string }) {
  const t = useTranslations("people.members.preview")
  if (!phone) return null
  const whatsapp = whatsappHref(phone)
  return (
    <>
      <Button variant='outline' size='sm' asChild className={buttonClassName}>
        <a href={`tel:${phone}`}>
          <PhoneIcon className='size-4' aria-hidden />
          {t("call")}
        </a>
      </Button>
      {whatsapp ? (
        <Button variant='outline' size='sm' asChild className={buttonClassName}>
          <a href={whatsapp} target='_blank' rel='noreferrer'>
            {t("whatsapp")}
          </a>
        </Button>
      ) : null}
    </>
  )
}

export function PreviewFact({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className='bg-muted/50 rounded-lg px-3.5 py-3'>
      <dt className='text-muted-foreground text-xs font-medium'>{label}</dt>
      <dd className={cn("mt-0.5 truncate font-semibold", muted && "text-muted-foreground font-medium")}>{value}</dd>
    </div>
  )
}
